import { Component, AfterViewInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CustomerService } from '@app/services/customer.service';
import { Asset, Event, UseCase } from '@app/core/interfaces';
import { USECASES, GET_NAME_OF_ASSET } from '@app/core/constants';
import { EventsService } from '@app/services/events.service';
import { StorageService } from '@app/services/storage.service';
import { WebSocketService } from '@app/services/websocket.service';
import { UsersService } from '@app/services/users.service';
import { SharedSendTagMsgComponent } from '@app/shared/shared-send-tag-msg/shared-send-tag-msg.component';
import { TranslateService } from '@ngx-translate/core';
// import { MapPreviewerComponent } from './map-previewer/map-previewer.component';

@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.scss'],
})
export class AlertsComponent implements AfterViewInit {
  @ViewChild('mapPreviewer', { static: false }) mapPreviewerChild: any;

  acknowledgeForm: UntypedFormGroup;
  actionForm: UntypedFormGroup;
  actionTypes: any = [];
  customAction: boolean = false;

  loadingEvent: boolean = false;
  preventActionsOnEvent: boolean = false;

  loggedInUser: any = {};
  sortOrder: string = 'date';
  systemSettings: any = {};
  useCaseFilter: number = 0;
  useCases: UseCase[] = [];
  userList: any = [];

  selectedEvent: Event | null = null;
  selectedStaff: Asset | null = null;
  staffAssociatedWithEvent: any = null;

  errorImages: Set<string> = new Set<string>();

  constructor(
    public customerService: CustomerService,
    public dialogRef: MatDialogRef<AlertsComponent>,
    public eventsService: EventsService,
    public fb: UntypedFormBuilder,
    public notifier: MatSnackBar,
    public storage: StorageService,
    public socketService: WebSocketService,
    public usersService: UsersService,
    public popupDialog: MatDialog,
    public translate: TranslateService
  ) {
    this.useCases = USECASES.filter((use: UseCase) => {
      return this.socketService.allAlerts
        .map((ev: any) => ev.UseCase)
        .includes(use.enumId);
    });

    this.customerService.systemSetting$.subscribe((sets) => {
      this.systemSettings = sets;
    });

    this.acknowledgeForm = this.fb.group({
      comment: [''],
    });

    this.actionForm = this.fb.group({
      action: [''],
      comment: [''],
      checked: [false],
      user: [this.loggedInUser, [Validators.required]],
    });

    this.eventsService.initializeService();
  }

  ngOnInit() {
    this.loggedInUser = this.storage.uvsCreds.userObject;
    this.filterEvents();
  }

  ngAfterViewInit(): void {
    this.eventsService.getEventAction().subscribe((res: any) => {
      this.actionTypes = res.List;
    });
    this.getUsers();
  }

  ngOnDestroy() {
    //console.log('destroying alerts popup component');
    this.sortOrder = 'date';
    this.selectedEvent = null;
    this.selectedStaff = null;
    this.useCaseFilter = 0;
  }

  acknowledgeEvent() {
    if (this.selectedEvent) {
      this.preventActionsOnEvent = true;
      const comment = this.acknowledgeForm.value.comment
        ? this.acknowledgeForm.value.comment
        : 0;
      this.eventsService
        .acknowledgeEvent(this.selectedEvent.Id, comment)
        .subscribe(
          () => {
            this.preventActionsOnEvent = false;
            this.socketService.allAlerts = this.socketService.allAlerts.map(
              (event: any) => {
                if (event.Id === this.selectedEvent?.Id) {
                  event.Status = 'Acknowledged';
                }
                return event;
              }
            );
            this.socketService.allAlerts = this.socketService.allAlerts.map(
              (event: any) => {
                if (event.Id === this.selectedEvent?.Id) {
                  event.Status = 'Acknowledged';
                }
                return event;
              }
            );
            this.selectEvent(this.selectedEvent);
            this.acknowledgeForm.setValue({ comment: '' }); //MKL 10-30-2023
            this.notifier.open('Event acknowledged.', '', {
              panelClass: 'success',
            });
          },
          () => {
            this.preventActionsOnEvent = false;
            this.notifier.open(
              'There was a problem acknowledging the event.',
              '',
              {
                panelClass: 'error',
              }
            );
          }
        );
    }
  }

  // if the event is an infra timeout, you can't close.
  // if the even is a safety switch alert or ss repeat alert...
  // and if there is no ruledef or no message, you can't close.
  // if there is a ruleDef and message, parse them into json.
  // if the XpertMessage shows that the switch is closed, you can close it.
  // if the switch is open and the rule definition says you can close it, you can close it

  canCloseEvent() {
    if (this.selectedEvent) {
      if (this.selectedEvent.SystemName === 'SYSTEM_INFRA_TIMEOUT_ALERT')
        return false;
      if (
        this.selectedEvent.SystemName === 'SAFETYSWITCH_ALERT' ||
        this.selectedEvent.SystemName === 'SAFETYSWITCHREPEAT_ALERT'
      ) {
        if (!this.selectedEvent?.RuleDef || !this.selectedEvent?.Message)
          return false;
        const xpertMessage: any = JSON.parse(this.selectedEvent.Message);
        const ruleDef: any = JSON.parse(this.selectedEvent.RuleDef);
        const switchOpen: boolean =
          xpertMessage.DeviceReports[0]?.Status?.Tamper2;
        const allowIfOpen: boolean =
          ruleDef.conditions[0]?.attr[0]?.AllowEventClose;
        if (!switchOpen) return true;
        if (switchOpen && allowIfOpen) return true;
        return false;
      }
      return true;
    } else {
      return false;
    }
  }

  //MKL Because Assets can have multiple tags now, alert the user that only a certain tag will receive it.
  checkMessageEligibility(): boolean {
    if (
      this.selectedEvent?.Name.includes('B4') ||
      this.selectedEvent?.PersonName.includes('B4')
    ) {
      return true;
    } else if (
      this.selectedEvent?.Name.includes('E50') ||
      this.selectedEvent?.PersonName.includes('E50')
    ) {
      return true;
    } else return false;
  }

  closeEvent() {
    if (this.selectedEvent) {
      this.preventActionsOnEvent = true;
      const form = this.actionForm.value;
      const eventId = this.selectedEvent.Id;
      const userId = form.user.Id;
      const comment: any = form.comment ? form.comment : 0;
      this.eventsService.closeEvent(comment, userId, eventId).subscribe(
        () => {
          this.preventActionsOnEvent = false;
          if (this.socketService.allAlerts.length === 0) {
            this.dialogRef.close();
          }
          if (this.socketService.allAlerts.length > 0) {
            this.filterEvents();
          }
          this.notifier.open('Alert closed.', '', {
            panelClass: 'success',
          });
        },
        () => {
          this.preventActionsOnEvent = false;
          this.notifier.open(
            'There was a problem closing the event action.',
            '',
            {
              panelClass: 'error',
            }
          );
        }
      );
    }
  }

  createEventAction() {
    if (this.selectedEvent) {
      this.preventActionsOnEvent = true;
      const form = this.actionForm.value;
      const comment: any = form.comment ? form.comment : 0;
      const actionId = this.customAction ? 0 : form.action.Id;
      const eventId = this.selectedEvent.Id;
      const actionName = this.customAction ? form.action : form.action.Name;
      const userId = form.user.Id;

      if (!actionName) {
        this.notifier.open(`Please submit an action.`, 'Close', {
          duration: 5000,
        });
        this.preventActionsOnEvent = false;
        return;
      }

      this.eventsService
        .eventAction(eventId, actionId, actionName, comment, userId)
        .subscribe(
          () => {
            this.preventActionsOnEvent = false;
            this.selectEvent(this.selectedEvent);
            this.notifier.open('Event action applied.', '', {
              panelClass: 'success',
            });
          },
          () => {
            this.preventActionsOnEvent = false;
            this.notifier.open(
              'There was a problem applying the event action.',
              '',
              {
                panelClass: 'error',
              }
            );
          }
        );
    }
  }

  filterEvents() {
    //console.log('filtering...');
    const events = this.socketService.allAlerts.filter((ev: any) => {
      if (this.useCaseFilter === 0) {
        return true;
      } else {
        return ev.UseCase === this.useCaseFilter;
      }
    });

    if (this.sortOrder === 'date') {
      //console.log(' by date');
      this.groupAlertByDate();
    } else if (this.sortOrder === 'status') {
      //console.log(' by status');
      this.groupAlertsByStatus();
    }
  }

  followStaff() {
    //change this
    this.selectedStaff = this.staffAssociatedWithEvent;
  }

  getColors(index: number) {
    return index % 2 === 0 ? '#f0f0f0' : 'white';
  }

  isSelected(event: any) {
    if (event != null) {
      return event.Id === this.selectedEvent?.Id ? '2px solid #1976d2' : 'none';
    } else return false;
  }

  getUsers() {
    this.usersService.getUsers().subscribe((response: any) => {
      this.userList = response.List;
    });
  }

  groupAlertsByStatus() {
    const sorter = (a: Event, b: Event) => {
      return a.Status == 'Open' ? 0 : 1;
    };
    this.socketService.allAlerts = this.socketService.allAlerts.sort(sorter);
  }

  groupAlertByDate() {
    const sorter = (a: Event, b: Event) => {
      const dateA = new Date(a.EventTime).getTime();
      const dateB = new Date(b.EventTime).getTime();
      return dateB - dateA;
    };
    //console.log(new Date(this.socketService.allAlerts[0].EventTime).getTime());
    this.socketService.allAlerts = this.socketService.allAlerts.sort(sorter);
  }

  resetForm() {
    this.actionForm.setValue({
      action: '',
      comment: '',
      user: this.loggedInUser,
      checked: false,
    });
    this.customAction = false;
  }

  selectEvent(event: any) {
    if (event.Id > 0) {
      this.loadingEvent = true;
      this.eventsService.getEventById(event.Id).subscribe(
        (ev: any) => {
          this.selectedEvent = ev;
          this.eventsService.addZoneInfo(ev);
          this.loadingEvent = false;
          if (ev.ItemId !== 0) {
            this.eventsService
              .getStaffById(ev.ItemId)
              .subscribe((staff: any) => {
                this.staffAssociatedWithEvent = staff;
              });
          }
          this.resetForm();
        },
        (err: any) => {
          this.notifier.open(this.translate.instant('NOTIFIER.ERROR_GET'), '', {
            panelClass: 'error',
          });
          this.selectedEvent = null;
          this.selectedStaff = null;
          this.loadingEvent = false;
        }
      );
    } else {
      console.log('no event ID');
    }
  }

  sendMessage() {
    if (this.selectedEvent) {
      const dialogRef = this.popupDialog.open(SharedSendTagMsgComponent, {
        data: { Id: this.selectedEvent.ItemId },
        height: 'calc(100% - 50px)',
        position: { right: '0px', top: '50px' },
        width: '750px',
      });
    }
  }

  toggleCustomAction() {
    this.customAction = !this.customAction;
    this.actionForm.get('action')?.setValue('');
  }

  handleImageError(event: any) {
    const imgSrc = event.target.src;
    if (!this.errorImages.has(imgSrc)) {
      this.errorImages.add(imgSrc);
      event.target.src = '../../assets/images/noMapImage.jpg';
    }
  }

  // AREA_ALERT
  // BATTERY_ALERT
  // BLEPROXIMITY_ALERT
  // BUTTON_ALERT
  // CONTAMINATION_ALERT
  // DWELLABSENCE_ALERT
  // HOME_ZONE_VIOLATION_ALERT
  // MOTION_ALERT
  // nodetestingeventalert
  // PARLEVEL_ALERT
  // ROUTE_ALERT
  // SAFETYSWITCH_ALERT
  // SAFETYSWITCHREPEAT_ALERT
  // SDC_ALERT
  // SYSTEM_INFRA_TIMEOUT_ALERT
  // TEMPERATURE_ALERT
  // WAIT_TIME_ALERT
  getTranslationFromAlertSystemName(systemName: string): string {
    console.log(systemName);
    let parsedZoneNames = [{ ZoneName: 'N/A' }];
    try {
      parsedZoneNames = JSON.parse(this.selectedEvent?.ZoneName);
    } catch (err) {
      console.log('zone name parse failure');
    }
    return this.translate.instant('ALERT_SYSTEM_NAMES.' + systemName, {
      AllowedValueRange: this.selectedEvent?.AllowedValueRange,
      MinValue: this.selectedEvent?.MinValue,
      MaxValue: this.selectedEvent?.MaxValue,
      PersonAssociated: this.selectedEvent?.PersonAssociated,
      RuleSetMajorVersion: this.selectedEvent?.RuleSetMajorVersion,
      RuleSetMinorVersion: this.selectedEvent?.RuleSetMinorVersion,
      ViolationValue: this.selectedEvent?.ViolationValue,
      ZoneName: parsedZoneNames[0]?.ZoneName,
    });
  }
}
